import { useMemo, useState } from 'react';
import type { Broadcast, BroadcastPlacement, MessagePreset } from '@noorview/core';
import { APPEAL_LENGTHS, MESSAGE_LENGTHS, MESSAGE_PRESETS, addDays, appealTotals, editEndChoices, editTimingProblem, editedEnd, giftLine, hhmm, isBroadcastLive, money, presetOf, ymd, zonedToInstant } from '@noorview/core';
import { attempt, backend, useApp } from '../data/store';
import { Segmented, Sheet, confirm } from '../ui/controls';
import { DateInput, Select, TimeInput } from '../ui/pickers';
import { AreaField, NumberField, TextField } from '../ui/fields';
import { Icon } from '../ui/icons';
import { PositionPicker } from '../ui/PositionPicker';
import { openSheet, type SheetState } from '../app/ui';

/**
 * Urgent messages (Janazah, a car to move, an emergency…) and live appeals, sent from the dashboard straight to every
 * screen or chosen screens. They sit on top of whatever the screen shows until they end.
 */


/** Every screen, or the ones ticked. */
function ScreenPicker({ value, onChange }: { value: string[] | null; onChange: (v: string[] | null) => void }) {
  const screens = useApp((s) => s.ws!.screens);
  return (
    <div className="field"><span>Screens</span>
      <Segmented size="sm" label="Screens" value={value ? 'some' : 'all'} onChange={(v) => onChange(v === 'all' ? null : (value ?? screens.slice(0, 1).map((s) => s.id)))}
        options={[{ value: 'all', label: 'All screens' }, { value: 'some', label: 'Choose screens' }]} />
      {value && (
        <div className="bc-screens" role="group" aria-label="Choose screens">
          {screens.map((s) => {
            const on = value.includes(s.id);
            return <button key={s.id} type="button" className={`cat${on ? ' on' : ''}`} aria-pressed={on} onClick={() => onChange(on ? value.filter((x) => x !== s.id) : [...value, s.id])}>{on && <Icon name="check" size={14} stroke={3} />}{s.name}</button>;
          })}
        </div>
      )}
    </div>
  );
}

const where = (b: Broadcast, names: Map<string, string>) => (b.screenIds ? b.screenIds.map((id) => names.get(id) ?? 'Screen').join(', ') : 'All screens');

/** Start time (now or later) and length, turned into the broadcast's window. */
function useWhen(tz: string) {
  const now = new Date();
  const [later, setLater] = useState(false);
  const [date, setDate] = useState(ymd(now, tz));
  const [time, setTime] = useState(() => hhmm(new Date(now.getTime() + 30 * 60_000), tz));
  const window = (minutes: number) => {
    const start = later ? zonedToInstant(date, time, tz) : new Date();
    return { startsAt: start.toISOString(), endsAt: minutes > 0 ? new Date(start.getTime() + minutes * 60_000).toISOString() : null, past: later && start.getTime() < Date.now() - 60_000 };
  };
  const ui = (
    <div className="field"><span>When</span>
      <Segmented size="sm" label="When" value={later ? 'later' : 'now'} onChange={(v) => setLater(v === 'later')} options={[{ value: 'now', label: 'Straight away' }, { value: 'later', label: 'Later' }]} />
      {later && <div className="two" style={{ marginTop: 8 }}><DateInput label="Date" value={date} min={ymd(now, tz)} max={addDays(ymd(now, tz), 365)} onChange={setDate} /><TimeInput label="Time" value={time} onChange={setTime} /></div>}
    </div>
  );
  return { later, ui, window };
}

export function MessageSheet({ onClose }: { onClose: () => void }) {
  const ws = useApp((s) => s.ws)!;
  const tz = ws.masjid.timezone;
  const [preset, setPreset] = useState<MessagePreset>('janazah');
  const [heading, setHeading] = useState(presetOf('janazah').heading);
  const [body, setBody] = useState('');
  const [placement, setPlacement] = useState<BroadcastPlacement>('full');
  const [screens, setScreens] = useState<string[] | null>(null);
  const [minutes, setMinutes] = useState(30);
  const [busy, setBusy] = useState(false);
  const when = useWhen(tz);
  const names = useMemo(() => new Map(ws.screens.map((s) => [s.id, s.name])), [ws.screens]);
  const live = ws.broadcasts.filter((b) => b.kind === 'message' && (!b.endsAt || Date.parse(b.endsAt) > Date.now()));
  const p = presetOf(preset);
  const choose = (id: MessagePreset) => {
    const cur = presetOf(preset);
    // Swap in the new preset's wording unless it's been written over.
    if (!heading.trim() || heading === cur.heading) setHeading(presetOf(id).heading);
    if (!body.trim() || body === cur.body) setBody(presetOf(id).body);
    setPreset(id);
  };
  const w = when.window(minutes);
  const send = async () => {
    if (!heading.trim()) return;
    setBusy(true);
    const ok = await attempt(() => backend.startBroadcast({ kind: 'message', screenIds: screens, placement, startsAt: w.startsAt, endsAt: w.endsAt, message: { preset, heading: heading.trim(), body: body.trim() } }),
      when.later ? 'Message scheduled' : 'Message sent to the screens');
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <Sheet open onClose={onClose} title="Urgent message" subtitle="Show a message on your screens straight away, or at a set time." dock
      footer={<><button type="button" className="btn plain" onClick={onClose}>Cancel</button>
        <button type="button" className={`btn ${preset === 'emergency' ? 'danger' : 'primary'}`} disabled={busy || !heading.trim() || w.past || (screens?.length === 0)} onClick={send}>{busy ? 'Sending…' : when.later ? 'Schedule message' : 'Send to the screens now'}</button></>}>
      <div className="bc-presets" role="radiogroup" aria-label="Kind of message">
        {MESSAGE_PRESETS.map((x) => (
          <button key={x.id} type="button" role="radio" aria-checked={preset === x.id} className={`bc-preset${preset === x.id ? ' on' : ''}`} style={{ ['--bm' as string]: x.color }} onClick={() => choose(x.id)}>{x.label}</button>
        ))}
      </div>
      <TextField label="Heading" value={heading} max={60} onChange={setHeading} />
      <AreaField label="Message" value={body} max={240} rows={3} onChange={setBody} />
      <div className="field"><span>Show it</span>
        <PositionPicker value={placement} onChange={setPlacement} widget={{ label: 'In the announcements' }} />
        {placement === 'widget' && <small className="hint">Shows in place of each Announcements widget. Screens without one show it as a band.</small>}
        {placement === 'full' && <small className="hint">The time stays in the top corner, so everyone can still see it.</small>}
      </div>
      <ScreenPicker value={screens} onChange={setScreens} />
      <div className="two">
        {when.ui}
        <div className="field"><span>For how long</span><Select label="For how long" value={String(minutes)} onChange={(v) => setMinutes(Number(v))} options={MESSAGE_LENGTHS.map(([v, l]) => ({ value: String(v), label: l }))} /></div>
      </div>
      {w.past && <div className="banner bad">That time has already passed.</div>}
      <div className="bc-preview" style={{ ['--bm' as string]: p.color }} aria-hidden><small>{p.label}</small><b>{heading || p.heading}</b>{body && <span>{body}</span>}</div>
      {live.length > 0 && (
        <div className="stack">
          <div className="section-label">On the screens now and coming up</div>
          {live.map((b) => (
            <div key={b.id} className="row sched ctl">
              <i className="bc-dot" style={{ background: presetOf(b.message?.preset).color }} />
              <span style={{ flex: 1, minWidth: 0 }}><b>{b.message?.heading}</b><small className="hint" style={{ display: 'block' }}>{isBroadcastLive(b, new Date()) ? 'Showing now' : `From ${hhmm(new Date(b.startsAt), tz)}`}{b.endsAt ? ` until ${hhmm(new Date(b.endsAt), tz)}` : ' until ended'} · {where(b, names)}</small></span>
              <button type="button" className="btn sm secondary" onClick={() => openSheet({ name: 'editBroadcast', id: b.id, back: { name: 'message' } })}>Edit</button>
              <button type="button" className="btn sm plain" onClick={() => attempt(() => backend.endBroadcast(b.id), 'Message taken down')}>End</button>
            </div>
          ))}
        </div>
      )}
    </Sheet>
  );
}

export function AppealSheet({ onClose }: { onClose: () => void }) {
  const ws = useApp((s) => s.ws)!;
  const current = ws.broadcasts.filter((b) => b.kind === 'appeal' && (!b.endsAt || Date.parse(b.endsAt) > Date.now())).at(-1);
  return current ? <ManageAppeal b={current} onClose={onClose} /> : <StartAppeal onClose={onClose} />;
}

function StartAppeal({ onClose }: { onClose: () => void }) {
  const ws = useApp((s) => s.ws)!;
  const [title, setTitle] = useState('Masjid extension appeal');
  const [target, setTarget] = useState(50000);
  const [placement, setPlacement] = useState<BroadcastPlacement>('full');
  const [screens, setScreens] = useState<string[] | null>(null);
  const [minutes, setMinutes] = useState(0);
  const [busy, setBusy] = useState(false);
  const when = useWhen(ws.masjid.timezone);
  const w = when.window(minutes);
  const start = async () => {
    setBusy(true);
    await attempt(() => backend.startBroadcast({ kind: 'appeal', screenIds: screens, placement, startsAt: w.startsAt, endsAt: w.endsAt, appeal: { title: title.trim() || 'Appeal', target: Math.max(0, target), currency: '£', gifts: [] } }),
      when.later ? 'Appeal scheduled' : 'Live appeal started');
    setBusy(false);
  };
  return (
    <Sheet open onClose={onClose} title="Live appeal" subtitle="Show the total rising on your screens as gifts come in." dock
      footer={<><button type="button" className="btn plain" onClick={onClose}>Cancel</button><button type="button" className="btn primary" disabled={busy || w.past || screens?.length === 0} onClick={start}>{busy ? 'Starting…' : when.later ? 'Schedule appeal' : 'Start the appeal'}</button></>}>
      <div className="two">
        <TextField label="What it’s for" value={title} max={60} onChange={setTitle} />
        <NumberField label="Target (£)" value={target} min={0} max={10_000_000} step={1000} onChange={setTarget} />
      </div>
      <div className="field"><span>Show it</span>
        <PositionPicker value={placement} onChange={setPlacement} widget={{ label: 'In the donations' }} only={['full', 'left', 'half', 'center']} />
        {placement === 'widget' && <small className="hint">Shows in place of each Donations widget. Screens without one show it on half the screen.</small>}
      </div>
      <ScreenPicker value={screens} onChange={setScreens} />
      <div className="two">
        {when.ui}
        <div className="field"><span>For how long</span><Select label="For how long" value={String(minutes)} onChange={(v) => setMinutes(Number(v))} options={APPEAL_LENGTHS.map(([v, l]) => ({ value: String(v), label: l }))} /></div>
      </div>
      {w.past && <div className="banner bad">That time has already passed.</div>}
    </Sheet>
  );
}

function ManageAppeal({ b, onClose }: { b: Broadcast; onClose: () => void }) {
  const ws = useApp((s) => s.ws)!;
  const a = b.appeal!;
  const t = appealTotals(a);
  const [amount, setAmount] = useState('');
  const [name, setName] = useState('');
  const [gender, setGender] = useState<'brother' | 'sister'>('brother');
  const [busy, setBusy] = useState(false);
  const names = useMemo(() => new Map(ws.screens.map((s) => [s.id, s.name])), [ws.screens]);
  const n = Number(amount.replace(/[^\d.]/g, ''));
  const add = async () => {
    if (!(n > 0)) return;
    setBusy(true);
    const ok = await attempt(() => backend.addGift(b.id, { amount: n, name: name.trim(), gender }), `${money(n, a.currency)} added`);
    setBusy(false);
    if (ok) { setAmount(''); setName(''); }
  };
  const end = async () => {
    if (await confirm('End the appeal?', { body: `It comes off every screen now. ${money(t.raised, a.currency)} was raised from ${t.count} gift${t.count === 1 ? '' : 's'}.`, ok: 'End the appeal', danger: true })) {
      if (await attempt(() => backend.endBroadcast(b.id), 'Appeal ended')) onClose();
    }
  };
  const live = isBroadcastLive(b, new Date());
  return (
    <Sheet open onClose={onClose} title="Live appeal" subtitle={`${a.title} · ${live ? 'on' : 'starts at ' + hhmm(new Date(b.startsAt), ws.masjid.timezone) + ' on'} ${where(b, names)}`} dock>
      <div className="ap-total">
        <b className="mono">{money(t.raised, a.currency)}</b>
        <span className="hint">{a.target > 0 ? `raised of ${money(a.target, a.currency)} (${t.pct}%)` : 'raised'}, {t.count} gift{t.count === 1 ? '' : 's'}</span>
        {a.target > 0 && <span className="ap-bar"><i style={{ width: `${t.pct}%` }} /></span>}
      </div>
      <div className="two">
        <label className="field"><span>Amount (£)</span><input className="input mono" inputMode="decimal" placeholder="e.g. 250" value={amount} onChange={(e) => setAmount(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void add(); }} /></label>
        <label className="field"><span>Name (optional)</span><input className="input" placeholder="Anonymous" maxLength={60} value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void add(); }} /></label>
      </div>
      <Segmented label="Given by" value={gender} onChange={setGender} options={[{ value: 'brother', label: 'Brother' }, { value: 'sister', label: 'Sister' }]} />
      <button type="button" className="btn primary block" disabled={busy || !(n > 0)} onClick={add}>Add this gift</button>
      {a.gifts.length > 0 && (
        <div className="stack ap-gifts">
          <div className="section-label">Gifts</div>
          {[...a.gifts].reverse().slice(0, 30).map((g) => (
            <div key={g.id} className="row sched ctl">
              <span style={{ flex: 1, minWidth: 0 }}>{giftLine(g, a.currency)}<small className="hint" style={{ display: 'block' }}>{hhmm(new Date(g.at), ws.masjid.timezone)}</small></span>
              <button type="button" className="lbtn danger" aria-label="Remove this gift" onClick={() => attempt(() => backend.removeGift(b.id, g.id), 'Gift removed')}><Icon name="trash" size={16} /></button>
            </div>
          ))}
        </div>
      )}
      <button type="button" className="btn secondary block" onClick={() => openSheet({ name: 'editBroadcast', id: b.id, back: { name: 'appeal' } })}><Icon name="pencil" size={18} />Edit the appeal</button>
      <button type="button" className="btn plain danger-text block" onClick={end}>End the appeal</button>
    </Sheet>
  );
}

/**
 * Editing a message or appeal that's on the screens or waiting to start: its wording, where and on which screens it
 * shows, and when it ends (and when it starts, if it hasn't yet). It stays on the screens throughout; saving updates
 * them within seconds. An appeal's gifts are never touched.
 */
export function EditBroadcastSheet({ id, back, onClose }: { id: string; back?: SheetState; onClose: () => void }) {
  const ws = useApp((s) => s.ws)!;
  const tz = ws.masjid.timezone;
  const b = ws.broadcasts.find((x) => x.id === id);
  const [now] = useState(() => new Date());
  const started = !!b && Date.parse(b.startsAt) <= now.getTime();
  const [preset, setPreset] = useState<MessagePreset>(b?.message?.preset ?? 'notice');
  const [heading, setHeading] = useState(b?.message?.heading ?? '');
  const [body, setBody] = useState(b?.message?.body ?? '');
  const [title, setTitle] = useState(b?.appeal?.title ?? '');
  const [target, setTarget] = useState(b?.appeal?.target ?? 0);
  const [placement, setPlacement] = useState<BroadcastPlacement>(b?.placement ?? 'full');
  const [screens, setScreens] = useState<string[] | null>(b?.screenIds ?? null);
  const [date, setDate] = useState(() => (b ? ymd(new Date(b.startsAt), tz) : ''));
  const [time, setTime] = useState(() => (b ? hhmm(new Date(b.startsAt), tz) : ''));
  const [end, setEnd] = useState('keep');
  const [busy, setBusy] = useState(false);
  const leave = () => { if (back) openSheet(back); else onClose(); };
  // Ended or removed while open (by someone else, or it ran out).
  if (!b || (b.endsAt && Date.parse(b.endsAt) <= Date.now())) {
    return (
      <Sheet open onClose={onClose} title="Already ended" footer={<button type="button" className="btn primary" onClick={leave}>OK</button>}>
        <p className="hint" style={{ margin: 0 }}>This {b?.kind === 'appeal' ? 'appeal' : 'message'} has already come off the screens, so there’s nothing to edit.</p>
      </Sheet>
    );
  }
  const appeal = b.kind === 'appeal';
  const startsAt = started ? b.startsAt : zonedToInstant(date, time, tz).toISOString();
  const startChanged = !started && startsAt !== b.startsAt;
  const endsAt = editedEnd(b, end, startsAt, new Date());
  const problem = editTimingProblem(startsAt, endsAt, startChanged, new Date());
  const p = presetOf(preset);
  const choose = (x: MessagePreset) => {
    const cur = presetOf(preset);
    if (!heading.trim() || heading === cur.heading) setHeading(presetOf(x).heading);
    if (!body.trim() || body === cur.body) setBody(presetOf(x).body);
    setPreset(x);
  };
  const invalid = !!problem || screens?.length === 0 || (appeal ? !title.trim() : !heading.trim());
  const save = async () => {
    if (invalid) return;
    setBusy(true);
    const ok = await attempt(() => backend.updateBroadcast(b.id, {
      ...(startChanged ? { startsAt } : {}),
      endsAt, screenIds: screens, placement,
      ...(appeal ? { title: title.trim(), target: Math.max(0, target) } : { message: { ...b.message!, preset, heading: heading.trim(), body: body.trim() } }),
    }), appeal ? 'Appeal updated' : 'Message updated');
    setBusy(false);
    if (ok) leave();
  };
  const widgetOption = appeal ? { label: 'In the donations' } : { label: b.message?.widgetId ? 'In its widget' : 'In the announcements' };
  return (
    <Sheet open onClose={onClose} title={appeal ? 'Edit the appeal' : 'Edit the message'} subtitle={started ? 'It stays on the screens while you edit. Saving updates them within seconds.' : `Starts ${ymd(new Date(b.startsAt), tz) === ymd(now, tz) ? 'today' : 'on ' + ymd(new Date(b.startsAt), tz)} at ${hhmm(new Date(b.startsAt), tz)}.`} dock
      footer={<><button type="button" className="btn plain" onClick={leave}>Cancel</button>
        <button type="button" className={`btn ${!appeal && preset === 'emergency' ? 'danger' : 'primary'}`} disabled={busy || invalid} onClick={save}>{busy ? 'Saving…' : 'Save changes'}</button></>}>
      {appeal ? (
        <div className="two">
          <TextField label="What it’s for" value={title} max={60} onChange={setTitle} />
          <NumberField label="Target (£)" value={target} min={0} max={10_000_000} step={1000} onChange={setTarget} />
        </div>
      ) : (<>
        <div className="bc-presets" role="radiogroup" aria-label="Kind of message">
          {MESSAGE_PRESETS.map((x) => (
            <button key={x.id} type="button" role="radio" aria-checked={preset === x.id} className={`bc-preset${preset === x.id ? ' on' : ''}`} style={{ ['--bm' as string]: x.color }} onClick={() => choose(x.id)}>{x.label}</button>
          ))}
        </div>
        <TextField label="Heading" value={heading} max={60} onChange={setHeading} />
        <AreaField label="Message" value={body} max={240} rows={3} onChange={setBody} />
      </>)}
      <div className="field"><span>Show it</span>
        <PositionPicker value={placement} onChange={setPlacement} widget={widgetOption} only={appeal ? ['full', 'left', 'half', 'center'] : undefined} />
      </div>
      <ScreenPicker value={screens} onChange={setScreens} />
      <div className="two">
        {started
          ? <div className="field"><span>Started</span><p className="hint" style={{ margin: '10px 0 0' }}>On the screens since {hhmm(new Date(b.startsAt), tz)}</p></div>
          : <div className="field"><span>Starts</span><div className="two"><DateInput label="Date" value={date} min={ymd(now, tz)} max={addDays(ymd(now, tz), 365)} onChange={setDate} /><TimeInput label="Time" value={time} onChange={setTime} /></div></div>}
        <div className="field"><span>Ends</span><Select label="Ends" value={end} onChange={setEnd} options={editEndChoices(b, tz, now, startsAt)} /></div>
      </div>
      {problem && <div className="banner bad">{problem}</div>}
      {!appeal && <div className="bc-preview" style={{ ['--bm' as string]: b.message?.color || p.color, ...(b.message?.textColor ? { color: b.message.textColor } : {}) }} aria-hidden><small>{p.label}</small><b>{heading || p.heading}</b>{body && <span>{body}</span>}</div>}
    </Sheet>
  );
}
